import { useCallback, useMemo } from "react";
import {
  HiddenCategoryTypes,
  useGetChartOfAccountsQuery,
} from "store/apis/chartOfAccounts";
import { TxnCategories } from "types/Models/books";
import { useCurrentEntityId } from "./useCurrentEntityId";
import { useCurrentGroupContext } from "./useCurrentGroupContext";
import { useAppSelector } from "./useAppSelector";

type Args = {
  search?: string;
  hiddenCategoryTypes?: HiddenCategoryTypes[];
  root_category_id?: string;
};

export const useChartOfAccounts = ({
  search,
  hiddenCategoryTypes,
  root_category_id,
}: Args) => {
  const { uuid } = useCurrentGroupContext();
  const entity = useCurrentEntityId();
  const { groupId: groupUuid, entityId: entityUuid } = useAppSelector(
    (state) => state.openItem.addRequestInfoModal
  );
  const groupId = groupUuid || uuid;
  const entityId = entityUuid || entity;

  const { data, ...options } = useGetChartOfAccountsQuery(
    {
      groupId,
      entityId,
      hiddenCategoryTypes,
      root_category_id,
    },
    { skip: !groupId || !entityId }
  );

  const { types = [], opening_balance_summary } = data || {};

  const getTopParent = useCallback(
    (categoryId: string) => {
      const findCategory = (
        types: TxnCategories[],
        id: string
      ): null | TxnCategories => {
        let result = null;

        for (let item of types) {
          if (item.uuid === id) {
            result = item;
            break;
          } else if (item.types) {
            result = findCategory(item.types, id);
            if (result) {
              break;
            }
          }
        }

        return result;
      };

      return types.find((item) => {
        if (!item.types) {
          return false;
        }

        const result = findCategory(item.types, categoryId);
        if (result) {
          return result;
        }

        return false;
      });
    },
    [types]
  );

  const searchCategories = useCallback(
    (types: TxnCategories[]) => {
      let results = [];
      for (let item of types) {
        if (
          item.name.toLowerCase().includes(search || "") ||
          item.identifier.toString().includes(search || "")
        ) {
          results.push(item);
        } else if (item.types) {
          const nestedResults = searchCategories(item.types);
          if (Boolean(nestedResults.length)) {
            const newItem = { ...item };
            newItem.types = nestedResults;
            results.push(newItem);
          }
        }
      }
      return results;
    },
    [search]
  );

  const chartOfAccounts = useMemo(() => {
    if (search) {
      return searchCategories(types);
    }
    return types;
  }, [types, search]);

  return {
    chartOfAccounts,
    opening_balance_summary,
    ...options,
    getTopParent,
    searchCategories,
  };
};
